import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import {
  PageSplashscreenModelDefaultInitializerService,
  PageSplashscreenModelProgressInitializerService,
  PageSplashscreenModelSpinnerInitializerService,
} from '@gfc-flash/page-splashscreen';
import { PageRegisterModelInitializerService } from '@gfc-flash/page-register';
import { PageLoginModelInitializerService } from '@gfc-flash/page-login';
import {
  PageErrorModel0InitializerService,
  PageErrorModel1407InitializerService,
  PageErrorModel2405InitializerService,
  PageErrorModel2409InitializerService,
  PageErrorModel400InitializerService,
  PageErrorModel401InitializerService,
  PageErrorModel404InitializerService,
  PageErrorModel500InitializerService,
  PageErrorModel503InitializerService,
  PageErrorModel504InitializerService,
  PageErrorModel599InitializerService,
  PageErrorModel6405InitializerService,
  PageErrorModel7415InitializerService,
  PageErrorModel99499InitializerService,
} from '@gfc-flash/page-error';

/**
 * @description Applies application-level page models to shared GFC page packages.
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalPagesInitializerService {
  private readonly pageErrorModel0InitializerService: PageErrorModel0InitializerService =
    inject(PageErrorModel0InitializerService);
  private readonly pageErrorModel400InitializerService: PageErrorModel400InitializerService =
    inject(PageErrorModel400InitializerService);
  private readonly pageErrorModel401InitializerService: PageErrorModel401InitializerService =
    inject(PageErrorModel401InitializerService);
  private readonly pageErrorModel404InitializerService: PageErrorModel404InitializerService =
    inject(PageErrorModel404InitializerService);
  private readonly pageErrorModel500InitializerService: PageErrorModel500InitializerService =
    inject(PageErrorModel500InitializerService);
  private readonly pageErrorModel503InitializerService: PageErrorModel503InitializerService =
    inject(PageErrorModel503InitializerService);
  private readonly pageErrorModel504InitializerService: PageErrorModel504InitializerService =
    inject(PageErrorModel504InitializerService);
  private readonly pageErrorModel599InitializerService: PageErrorModel599InitializerService =
    inject(PageErrorModel599InitializerService);
  private readonly pageErrorModel1407InitializerService: PageErrorModel1407InitializerService =
    inject(PageErrorModel1407InitializerService);
  private readonly pageErrorModel2405InitializerService: PageErrorModel2405InitializerService =
    inject(PageErrorModel2405InitializerService);
  private readonly pageErrorModel2409InitializerService: PageErrorModel2409InitializerService =
    inject(PageErrorModel2409InitializerService);
  private readonly pageErrorModel6405InitializerService: PageErrorModel6405InitializerService =
    inject(PageErrorModel6405InitializerService);
  private readonly pageErrorModel7415InitializerService: PageErrorModel7415InitializerService =
    inject(PageErrorModel7415InitializerService);
  private readonly pageErrorModel99499InitializerService: PageErrorModel99499InitializerService =
    inject(PageErrorModel99499InitializerService);
  private readonly pageLoginModelInitializerService: PageLoginModelInitializerService =
    inject(PageLoginModelInitializerService);
  private readonly pageRegisterModelInitializerService: PageRegisterModelInitializerService =
    inject(PageRegisterModelInitializerService);
  private readonly pageSplashscreenModelDefaultInitializerService: PageSplashscreenModelDefaultInitializerService =
    inject(PageSplashscreenModelDefaultInitializerService);
  private readonly pageSplashscreenModelProgressInitializerService: PageSplashscreenModelProgressInitializerService =
    inject(PageSplashscreenModelProgressInitializerService);
  private readonly pageSplashscreenModelSpinnerInitializerService: PageSplashscreenModelSpinnerInitializerService =
    inject(PageSplashscreenModelSpinnerInitializerService);

  constructor() {}

  /**
   * @description Initializes error, authentication and splashscreen page models.
   */
  async initializePages() {
    this.pageErrorInitializer();
    this.pageLoginInitializer();
    this.pageRegisterInitializer();
    this.pageSplashscreenInitializer();
  }

  /**
   * @description Resets every configured error page model to its package default.
   */
  pageErrorInitializer() {
    this.pageErrorModel0InitializerService.setModel({
      ...this.pageErrorModel0InitializerService.getDefaultModel(),
    });
    this.pageErrorModel400InitializerService.setModel({
      ...this.pageErrorModel400InitializerService.getDefaultModel(),
    });
    this.pageErrorModel401InitializerService.setModel({
      ...this.pageErrorModel401InitializerService.getDefaultModel(),
    });
    this.pageErrorModel404InitializerService.setModel({
      ...this.pageErrorModel404InitializerService.getDefaultModel(),
    });
    this.pageErrorModel500InitializerService.setModel({
      ...this.pageErrorModel500InitializerService.getDefaultModel(),
    });
    this.pageErrorModel503InitializerService.setModel({
      ...this.pageErrorModel503InitializerService.getDefaultModel(),
    });
    this.pageErrorModel504InitializerService.setModel({
      ...this.pageErrorModel504InitializerService.getDefaultModel(),
    });
    this.pageErrorModel599InitializerService.setModel({
      ...this.pageErrorModel599InitializerService.getDefaultModel(),
    });
    this.pageErrorModel1407InitializerService.setModel({
      ...this.pageErrorModel1407InitializerService.getDefaultModel(),
    });
    this.pageErrorModel2405InitializerService.setModel({
      ...this.pageErrorModel2405InitializerService.getDefaultModel(),
    });
    this.pageErrorModel2409InitializerService.setModel({
      ...this.pageErrorModel2409InitializerService.getDefaultModel(),
    });
    this.pageErrorModel6405InitializerService.setModel({
      ...this.pageErrorModel6405InitializerService.getDefaultModel(),
    });
    this.pageErrorModel7415InitializerService.setModel({
      ...this.pageErrorModel7415InitializerService.getDefaultModel(),
    });
    this.pageErrorModel99499InitializerService.setModel({
      ...this.pageErrorModel99499InitializerService.getDefaultModel(),
    });
  }

  /**
   * @description Resets the login page model to its package default.
   */
  pageLoginInitializer() {
    this.pageLoginModelInitializerService.setModel({
      ...this.pageLoginModelInitializerService.getDefaultModel(),
    });
  }

  /**
   * @description Resets the register page model to its package default.
   */
  pageRegisterInitializer() {
    this.pageRegisterModelInitializerService.setModel({
      ...this.pageRegisterModelInitializerService.getDefaultModel(),
    });
  }

  /**
   * @description Configures splashscreen models with event support and the current app version.
   */
  pageSplashscreenInitializer() {
    this.pageSplashscreenModelDefaultInitializerService.setModel({
      ...this.pageSplashscreenModelDefaultInitializerService.getDefaultModel(),
      useEvents: true,
      version: {
        color: 'light',
        label: environment.version,
      },
    });
    this.pageSplashscreenModelProgressInitializerService.setModel({
      ...this.pageSplashscreenModelProgressInitializerService.getDefaultModel(),
      useEvents: true,
      version: {
        color: 'light',
        label: environment.version,
      },
    });
    this.pageSplashscreenModelSpinnerInitializerService.setModel({
      ...this.pageSplashscreenModelSpinnerInitializerService.getDefaultModel(),
      useEvents: true,
      version: {
        color: 'light',
        label: environment.version,
      },
    });
  }
}
